import styles from './styles.module.scss';

import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Board } from '../../../../../../components/Board';
import { NavButton } from '../../../../../../components/NavButton';
import { ProfileBox } from '../../../../../../components/ProfileBox';
import { SelectionList } from '../../../../../../components/SelectionList';
import { SelectionItem } from '../../../../../../components/SelectionListItem';
import { verifyRoutePermissions } from '../../../../../../globalFunctions';
import { MessageContext } from '../../../../../../providers/MessageProvider';
import { User, LoadData, Error, SchoolCourse } from '../../../../../../types';
import { LogicManagerCourseDetail } from '../LogicManagerCourseDetail';
import { LoadingContext } from '../../../../../../providers/LoadingProvider';
import { isEqual } from 'lodash';
import { AlertDialog, AlertDialogType } from '../../../../../../components/AlertDialog';
import { SchoolCourseDAO } from '../../../../../../classes/SchoolCourseDAO';
import { useSchoolCourse } from '../../../../../../hooks/useSchoolCourse';

export const LogicManagerCourseMaintainRoute = () => {
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const navigate = useNavigate();

    // Load data
    const courses: LoadData = useSchoolCourse();
    const [coursesList, setCoursesList] = useState<SelectionItem[]>();
    const [loadedCourses, setLoadedCourses] = useState<SchoolCourse[]>();
    const [selectedCourse, setSelectedCourse] = useState<SchoolCourse>();

    // Handle changes
    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const [alertDialogProps, setAlertDialogProps] = useState<AlertDialogType>();
    const [originalData, setOriginalData] = useState<SchoolCourse>();
    const { setIsLoadingContent } = useContext(LoadingContext);
    const { setOpenMessage, setMessage } = useContext(MessageContext);

    const save = async () => {
        if (selectedCourse) {
            try {
                setIsLoadingContent(true);

                const isNew = selectedCourse.id === 0;
                const result = await SchoolCourseDAO.save(userAuth.token, navigate, selectedCourse);

                if (
                    result &&
                    typeof result === 'object' &&
                    !Object.keys(result).includes('error')
                ) {
                    const currentCourse = Object.assign({}, result.course);
                    currentCourse.is_active = true;

                    setSelectedCourse({ ...currentCourse });
                    setOriginalData({ ...currentCourse });
                    updateCoursesList({ ...currentCourse }, false, isNew);
                    updateLoadedCourses({ ...currentCourse }, false, isNew);

                    setMessage({
                        type: 'success',
                        content: 'Curso salvo com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw result as Error;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const reset = () => {
        if (originalData) {
            setSelectedCourse({ ...originalData });
        }
    };

    const remove = async () => {
        if (selectedCourse) {
            try {
                const response = await SchoolCourseDAO.delete(
                    userAuth.token,
                    navigate,
                    selectedCourse.id
                );

                if (
                    response &&
                    typeof response === 'object' &&
                    Object.keys(response).includes('error')
                ) {
                    throw response as any;
                }

                setSelectedCourse(undefined);
                setOriginalData(undefined);
                updateCoursesList(selectedCourse, true);
                updateLoadedCourses(selectedCourse, true);

                setMessage({
                    type: 'success',
                    content: 'Curso excluído com sucesso'
                });
                setOpenMessage(true);
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    };

    const add = () => {
        const newCourse: SchoolCourse = {
            id: 0,
            name: '',
            slug: ''
        };

        setSelectedCourse({ ...newCourse });
        setOriginalData({ ...newCourse });
    };

    const onAdd = () => {
        if (isEqual(selectedCourse, originalData)) {
            add();
        } else {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: reset,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: async () => await save(),
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        }
    };

    const onSave = async () => {
        if (selectedCourse) {
            try {
                setIsLoadingContent(true);

                if (validate(selectedCourse)) {
                    if (!isEqual(selectedCourse, originalData)) {
                        await save();
                    } else {
                        setMessage({
                            type: 'info',
                            content: 'Sem alterações para salvar'
                        });
                        setOpenMessage(true);
                    }
                }
            } catch (error: any) {
                const e = error as Error;

                setMessage({
                    type: 'error',
                    content: e.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const onDelete = () => {
        setAlertDialogProps({
            title: 'Confirmar exclusão',
            message: 'Deseja realmente excluir este curso?',
            options: [
                {
                    text: 'Excluir',
                    action: async () => await remove(),
                    type: 'error'
                }
            ]
        });
        setOpenAlert(true);
    };

    const validate = (course: SchoolCourse) => {
        if (course.name.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo nome'
            });
            setOpenMessage(true);

            return false;
        }

        if (course.slug.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo sigla'
            });
            setOpenMessage(true);

            return false;
        }

        return true;
    };

    const onSelectCourse = (id: any) => {
        return new Promise<boolean>((resolve, reject) => {
            if (loadedCourses) {
                loadedCourses.forEach((course: SchoolCourse) => {
                    if (course.id === id) {
                        if (
                            selectedCourse &&
                            originalData &&
                            course.id !== originalData.id &&
                            !isEqual(selectedCourse, originalData)
                        ) {
                            setAlertDialogProps({
                                title: 'Confirmar alterações',
                                message: 'Deseja salvar as alterações?',
                                options: [
                                    {
                                        text: 'Desfazer',
                                        action: reset,
                                        type: 'error'
                                    },
                                    {
                                        text: 'Salvar',
                                        action: async () => await save(),
                                        type: 'success'
                                    }
                                ]
                            });
                            setOpenAlert(true);

                            resolve(false);
                        } else {
                            const currentSelectedCourse = Object.assign({}, course);
                            setSelectedCourse({ ...currentSelectedCourse });
                            setOriginalData({ ...currentSelectedCourse });

                            resolve(true);
                        }
                    }
                });
            }

            reject(false);
        });
    };

    const goBackwards = () => {
        if (isEqual(selectedCourse, originalData)) {
            navigate(`/manager`);
        } else {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: reset,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: async () => await save(),
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        }
    };

    const updateCoursesList = (
        currentCourse: SchoolCourse,
        wasRemoved: boolean = false,
        isNew: boolean = false
    ) => {
        if (coursesList) {
            const coursesData: SelectionItem[] = [];

            if (isNew) {
                coursesData.push({
                    id: currentCourse.id,
                    name: currentCourse.slug,
                    description: currentCourse.name
                });
            }

            coursesList.forEach((course: SelectionItem) => {
                if (course.id === currentCourse.id) {
                    if (!wasRemoved) {
                        coursesData.push({
                            id: currentCourse.id,
                            name: currentCourse.slug,
                            description: currentCourse.name
                        });
                    }
                } else {
                    coursesData.push(course);
                }
            });

            setCoursesList(coursesData);
        }
    };

    const updateLoadedCourses = (
        currentCourse: SchoolCourse,
        wasRemoved: boolean = false,
        isNew: boolean = false
    ) => {
        if (loadedCourses) {
            const coursesData: SchoolCourse[] = [];

            if (isNew) {
                coursesData.push(currentCourse);
            }

            loadedCourses.forEach((course: SchoolCourse) => {
                if (course.id === currentCourse.id) {
                    if (!wasRemoved) {
                        coursesData.push(currentCourse);
                    }
                } else {
                    coursesData.push(course);
                }
            });

            setLoadedCourses(coursesData);
        }
    };

    useEffect(() => {
        if (!courses.isLoading) {
            const data = courses.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                const coursesData: SelectionItem[] = [];

                courses.data.forEach((course: SchoolCourse) => {
                    coursesData.push({
                        id: course.id,
                        name: course.slug,
                        description: course.name
                    });
                });

                setCoursesList(coursesData);
                setLoadedCourses(data);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [courses.isLoading]);

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.admin'],
            navigate
        );

        if (typeof user === 'object') {
            setUserAuth(user);
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />
            {userAuth.token && coursesList && (
                <>
                    <ProfileBox user={userAuth} />
                    <Board wrapperClass={styles.board}>
                        <div className={styles.boardWrapper}>
                            <h1 className={styles.title}>Cursos</h1>

                            <div className={styles.editContainer}>
                                <SelectionList
                                    items={coursesList}
                                    defaultSelectedItem={
                                        selectedCourse &&
                                        ({
                                            id: selectedCourse.id,
                                            name: selectedCourse.slug,
                                            description: selectedCourse.name
                                        } as SelectionItem)
                                    }
                                    onSelectItem={onSelectCourse}
                                    searchPlaceholder='Pesquisar'
                                    wrapperClass={styles.list}
                                />
                                <LogicManagerCourseDetail
                                    course={selectedCourse}
                                    setCourse={setSelectedCourse}
                                    onSave={onSave}
                                    onAdd={onAdd}
                                    onDelete={onDelete}
                                />
                            </div>
                        </div>
                    </Board>
                </>
            )}
            {alertDialogProps && (
                <AlertDialog
                    setOpen={setOpenAlert}
                    open={openAlert}
                    title={alertDialogProps.title}
                    message={alertDialogProps.message}
                    options={alertDialogProps.options}
                />
            )}
        </div>
    );
};
